'use client';
import React from 'react';
import type { Filters } from './LessonLibraryClient';

const GRADE_OPTIONS = ['Elementary', 'Middle School', 'High School'];
const TOPIC_OPTIONS = [
  'Global Citizenship',
  'Identity & Culture',
  'Sustainability',
  'Global Issues',
  'Human Rights',
];
const SDG_OPTIONS = [1, 2, 4, 5, 6, 8, 10, 12, 13, 14, 15, 16, 17];
const DURATION_OPTIONS = ['1 Day', '2 Days', '3 Days', '1 Week'];

interface FilterSidebarProps {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
}

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">
        {title}
      </h3>
      {children}
    </div>
  );
}

export default function FilterSidebar({ filters, setFilters }: FilterSidebarProps) {
  const toggle = <T,>(key: keyof Filters, value: T) => {
    setFilters((prev) => {
      const arr = prev[key] as T[];
      return {
        ...prev,
        [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value],
      };
    });
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-100 p-5 shadow-card sticky top-20">
      <FilterSection title="Grade Band">
        {GRADE_OPTIONS.map((g) => (
          <label
            key={`filter-grade-${g}`}
            className="flex items-center gap-2.5 py-1.5 cursor-pointer group"
          >
            <input
              type="checkbox"
              checked={filters.grades.includes(g)}
              onChange={() => toggle<string>('grades', g)}
              className="w-4 h-4 rounded accent-primary-500 cursor-pointer"
            />
            <span className="text-sm text-stone-700 group-hover:text-stone-900 transition-colors">
              {g}
            </span>
          </label>
        ))}
      </FilterSection>

      <FilterSection title="Topic">
        {TOPIC_OPTIONS.map((t) => (
          <label
            key={`filter-topic-${t}`}
            className="flex items-center gap-2.5 py-1.5 cursor-pointer group"
          >
            <input
              type="checkbox"
              checked={filters.topics.includes(t)}
              onChange={() => toggle<string>('topics', t)}
              className="w-4 h-4 rounded accent-primary-500 cursor-pointer"
            />
            <span className="text-sm text-stone-700 group-hover:text-stone-900 transition-colors">
              {t}
            </span>
          </label>
        ))}
      </FilterSection>

      <FilterSection title="UN SDG">
        <div className="flex flex-wrap gap-1.5">
          {SDG_OPTIONS.map((sdg) => (
            <button
              key={`filter-sdg-${sdg}`}
              onClick={() => toggle<number>('sdgs', sdg)}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all duration-150 ${
                filters.sdgs.includes(sdg)
                  ? 'bg-blue-500 text-white' :'bg-blue-50 text-blue-600 hover:bg-blue-100'
              }`}
            >
              SDG {sdg}
            </button>
          ))}
        </div>
      </FilterSection>

      <FilterSection title="Lesson Duration">
        {DURATION_OPTIONS.map((d) => (
          <label
            key={`filter-dur-${d}`}
            className="flex items-center gap-2.5 py-1.5 cursor-pointer group"
          >
            <input
              type="checkbox"
              checked={filters.durations.includes(d)}
              onChange={() => toggle<string>('durations', d)}
              className="w-4 h-4 rounded accent-primary-500 cursor-pointer"
            />
            <span className="text-sm text-stone-700 group-hover:text-stone-900 transition-colors">
              {d}
            </span>
          </label>
        ))}
      </FilterSection>

      <div className="pt-2 border-t border-stone-100">
        <label className="flex items-center gap-2.5 py-1.5 cursor-pointer group">
          <input
            type="checkbox"
            checked={filters.freeOnly}
            onChange={() => setFilters((prev) => ({ ...prev, freeOnly: !prev.freeOnly }))}
            className="w-4 h-4 rounded accent-primary-500 cursor-pointer"
          />
          <span className="text-sm font-medium text-stone-700 group-hover:text-stone-900 transition-colors">
            Free lessons only
          </span>
        </label>
      </div>
    </div>
  );
}